import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RefundType } from 'features/Payment/services/refund'

export const SLICE_NAME = 'refunds'

export type RefundsParamsType = {
  orderId: string
  locationId: string | number
  status: string
  createdDateFrom: string | number
  createdDateTo: string | number
  pageSize: number
  pageIndex: number
  customer_id: string
  generatedBy: string
}

export type RefundsPaginationType = {
  pageSize: number
  currentItem: number
  currentPage: number
  totalData: number
  last: boolean
}

export type RefundsHubType = {
  location_id: number
  location_name: string
  [key: string]: unknown
}

export type RefundsRejectReasonType = {
  reason: string
  refund_rejection_reason_id: number
  [key: string]: unknown
}

export type RefundsComplaintReasonType = {
  name: string
  value: number | string
  isTextMandatory: boolean
}

export type RefundsImageRulesType = Record<string, unknown> | null

export type RefundsDetailType = Record<string, unknown> | null

export type RefundsFormType = Record<string, unknown> | null

export type RefundsRefundableProductsType = Record<string, unknown> | null

export interface RefundsStateType {
  params: RefundsParamsType
  pagination: RefundsPaginationType
  detail: RefundsDetailType
  form: RefundsFormType
  isLoading: boolean
  isOpenForm: boolean
  isRejectAction: boolean
  isOpenRejectNote: boolean
  rejectReason: string
  imagePreview: string
  rejectReasonList: RefundsRejectReasonType[]
  typeRefund: string
  data: RefundType[]
  hub: RefundsHubType[]
  isOpenPopupCreate: boolean
  dataRefundableProducts: RefundsRefundableProductsType
  complaintReasonList: RefundsComplaintReasonType[]
  refundImageRules: RefundsImageRulesType
}

const initialState: RefundsStateType = {
  params: {
    orderId: '',
    locationId: '',
    status: '',
    createdDateFrom: '',
    createdDateTo: '',
    pageSize: 20,
    pageIndex: 1,
    customer_id: '',
    generatedBy: '',
  },
  pagination: {
    pageSize: 10,
    currentItem: 0,
    currentPage: 0,
    totalData: 0,
    last: false,
  },
  detail: null,
  form: null,
  isLoading: false,
  isOpenForm: false,
  isRejectAction: false,
  isOpenRejectNote: false,
  rejectReason: '',
  imagePreview: '',
  rejectReasonList: [],
  typeRefund: '',
  data: [],
  hub: [],
  isOpenPopupCreate: false,
  dataRefundableProducts: null,
  complaintReasonList: [],
  refundImageRules: null,
}

const refunds = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setIsOpenForm: (state, action: PayloadAction<boolean>) => {
      state.isOpenForm = action.payload
      if (!action.payload) {
        state.form = null
        state.detail = null
      }
    },
    setData: (state, action: PayloadAction<RefundType[]>) => {
      state.data = action.payload
    },
    setHub: (state, action: PayloadAction<RefundsHubType[] | null | undefined>) => {
      state.hub = action.payload || []
    },
    setDetail: (state, action: PayloadAction<RefundsDetailType>) => {
      state.detail = action.payload
    },
    setForm: (state, action: PayloadAction<RefundsFormType>) => {
      state.form = action.payload
    },
    setPagination: (state, action: PayloadAction<RefundsPaginationType>) => {
      state.pagination = action.payload
    },
    setParams: (state, action: PayloadAction<RefundsParamsType>) => {
      state.params = action.payload
    },
    setImagePreview: (state, action: PayloadAction<string>) => {
      state.imagePreview = action.payload
    },
    setIsRejectAction: (state, action: PayloadAction<boolean>) => {
      state.isRejectAction = action.payload
      if (!action.payload) {
        state.rejectReason = ''
        state.rejectReasonList = []
      }
    },
    setIsOpenRejectNote: (state, action: PayloadAction<boolean>) => {
      state.isOpenRejectNote = action.payload
      if (!action.payload) state.rejectReason = ''
    },
    setRejectReason: (state, action: PayloadAction<string>) => {
      state.rejectReason = action.payload
    },
    setRejectReasonList: (state, action: PayloadAction<RefundsRejectReasonType[]>) => {
      state.rejectReasonList = action.payload
    },
    setTypeRefund: (state, action: PayloadAction<string>) => {
      state.typeRefund = action.payload
    },
    setDataRefundableProducts: (state, action: PayloadAction<RefundsRefundableProductsType>) => {
      state.dataRefundableProducts = action.payload
    },
    setIsOpenPopupCreate: (state, action: PayloadAction<boolean>) => {
      state.isOpenPopupCreate = action.payload
    },
    setComplaintReasonList: (state, action: PayloadAction<RefundsComplaintReasonType[]>) => {
      state.complaintReasonList = action.payload
    },
    setRefundImageRules: (state, action: PayloadAction<RefundsImageRulesType>) => {
      state.refundImageRules = action.payload
    },
    reset: () => initialState,
  },
})

export const {
  reset,
  setPagination,
  setIsOpenForm,
  setForm,
  setDetail,
  setIsLoading,
  setData,
  setParams,
  setImagePreview,
  setIsRejectAction,
  setIsOpenRejectNote,
  setRejectReason,
  setRejectReasonList,
  setHub,
  setTypeRefund,
  setDataRefundableProducts,
  setIsOpenPopupCreate,
  setComplaintReasonList,
  setRefundImageRules,
} = refunds.actions

export default refunds.reducer
